import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, Loader2, Plus, RefreshCw, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { formatPhoneNumber } from "@/lib/utils";

interface FMCSACarriersBulkModalProps {
  open: boolean;
  onClose: () => void;
  companyId: number;
  companyName: string;
  dotNumber: string | null;
  existingCarriers: any[];
  onSuccess: () => void;
}

export default function FMCSACarriersBulkModal({
  open,
  onClose,
  companyId,
  companyName,
  dotNumber,
  existingCarriers,
  onSuccess
}: FMCSACarriersBulkModalProps) {
  const { toast } = useToast();
  const [fmcsaCarriers, setFmcsaCarriers] = useState<any[]>([]);
  const [selectedActions, setSelectedActions] = useState<{ [docketNumber: string]: 'create' | 'update' | 'skip' }>({});
  const [hasFetched, setHasFetched] = useState(false);

  const fetchCarriersMutation = useMutation({
    mutationFn: async () => {
      if (!dotNumber) {
        throw new Error("Company does not have a DOT number");
      }

      const response = await apiRequest("POST", "/api/fmcsa/fetch-by-dot", {
        dotNumber,
      });

      const data = await response.json();
      return data;
    },
    onSuccess: (carriers) => {
      setHasFetched(true);
      setFmcsaCarriers(carriers);

      // Auto-determine actions for each carrier
      const actions: { [docketNumber: string]: 'create' | 'update' | 'skip' } = {};
      carriers.forEach((fmcsaCarrier: any) => {
        const docketFull = `${fmcsaCarrier.docketPrefix}-${fmcsaCarrier.docketNumber}`;
        const existingCarrier = existingCarriers.find(
          (c) => c.docketPrefix === fmcsaCarrier.docketPrefix && c.docketNumber === fmcsaCarrier.docketNumber
        );

        if (existingCarrier) {
          actions[docketFull] = 'update';
        } else {
          actions[docketFull] = 'create';
        }
      });

      setSelectedActions(actions);

      toast({
        title: "Success",
        description: `Found ${carriers.length} carrier(s) from FMCSA`,
      });
    },
    onError: (error: any) => {
      console.error('[FMCSA Bulk Fetch] Error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch carriers from FMCSA",
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (open && dotNumber && !hasFetched && !fetchCarriersMutation.isPending) {
      fetchCarriersMutation.mutate();
    }
  }, [open, dotNumber]);

  const applyChangesMutation = useMutation({
    mutationFn: async () => {
      const carriersToCreate: any[] = [];
      const carriersToUpdate: any[] = [];

      fmcsaCarriers.forEach((fmcsaCarrier) => {
        const docketFull = `${fmcsaCarrier.docketPrefix}-${fmcsaCarrier.docketNumber}`;
        const action = selectedActions[docketFull];

        if (action === 'skip') return;

        const carrierData = {
          ...fmcsaCarrier,
          companyId,
        };

        if (action === 'create') {
          carriersToCreate.push(carrierData);
        } else if (action === 'update') {
          const existingCarrier = existingCarriers.find(
            (c) => c.docketPrefix === fmcsaCarrier.docketPrefix && c.docketNumber === fmcsaCarrier.docketNumber
          );
          if (existingCarrier) {
            carriersToUpdate.push({ id: existingCarrier.id, data: carrierData });
          }
        }
      });

      // Create new carriers
      for (const carrier of carriersToCreate) {
        await apiRequest("POST", "/api/carriers", carrier);
      }

      // Update existing carriers
      for (const { id, data } of carriersToUpdate) {
        await apiRequest("PUT", `/api/carriers/${id}`, data);
      }

      return { created: carriersToCreate.length, updated: carriersToUpdate.length };
    },
    onSuccess: (result) => {
      // Invalidate both the main carriers list and the company-specific carriers
      queryClient.invalidateQueries({ queryKey: ["/api/carriers"] });
      queryClient.invalidateQueries({ queryKey: ["/api/companies", companyId, "carriers"] });
      
      toast({
        title: "Success",
        description: `Created ${result.created} and updated ${result.updated} carrier(s)`,
      });
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to apply changes",
        variant: "destructive",
      });
    },
  });

  const handleFetch = () => {
    fetchCarriersMutation.mutate();
  };

  const handleApply = () => {
    applyChangesMutation.mutate();
  };

  const toggleSkip = (docketFull: string) => {
    const currentAction = selectedActions[docketFull];
    const isExisting = existingCarriers.find((c) => `${c.docketPrefix}-${c.docketNumber}` === docketFull);

    if (currentAction === 'skip') {
      setSelectedActions({ ...selectedActions, [docketFull]: isExisting ? 'update' : 'create' });
    } else {
      setSelectedActions({ ...selectedActions, [docketFull]: 'skip' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Fetch Carriers from FMCSA</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>Company:</strong> {companyName}
            </p>
            <p className="text-sm text-gray-700">
              <strong>DOT Number:</strong> {dotNumber || 'Not set'}
            </p>
          </div>

          {!dotNumber && (
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">No DOT Number</p>
                <p className="text-sm text-yellow-700">
                  This company does not have a DOT number. Please add one in the company details to fetch carriers from FMCSA.
                </p>
              </div>
            </div>
          )}

          {fmcsaCarriers.length === 0 ? (
            <div className="text-center py-8">
              {fetchCarriersMutation.isPending ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <p className="text-gray-500">Fetching carriers from FMCSA...</p>
                </div>
              ) : fetchCarriersMutation.isError ? (
                <div className="space-y-3">
                  <p className="text-red-600">Failed to fetch carriers. Please try again.</p>
                  <Button onClick={handleFetch} variant="outline">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry
                  </Button>
                </div>
              ) : hasFetched ? (
                <p className="text-gray-500">No carriers found from FMCSA for this DOT number.</p>
              ) : (
                <p className="text-gray-500">
                  {dotNumber ? 'Loading...' : 'This company does not have a DOT number. Add one to fetch carriers.'}
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">Found {fmcsaCarriers.length} Carrier(s)</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleFetch}
                    disabled={fetchCarriersMutation.isPending}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>

                <p className="text-xs text-gray-500 italic">Uncheck a carrier to skip it during this update.</p>

                {fmcsaCarriers.map((carrier: any) => {
                  const docketFull = `${carrier.docketPrefix}-${carrier.docketNumber}`;
                  const action = selectedActions[docketFull] || 'create';
                  const isSkipped = action === 'skip';
                  const existingCarrier = existingCarriers.find(
                    (c) => c.docketPrefix === carrier.docketPrefix && c.docketNumber === carrier.docketNumber
                  );
                  const actionType = existingCarrier ? 'update' : 'create';

                  return (
                    <Card key={docketFull} className={`border-2 transition-opacity ${isSkipped ? 'opacity-50 border-gray-200' : actionType === 'update' ? 'border-blue-200' : 'border-green-200'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="pt-1">
                            <Checkbox
                              checked={!isSkipped}
                              onCheckedChange={() => toggleSkip(docketFull)}
                              aria-label={`Include ${carrier.name}`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <h4 className="font-medium">{carrier.name}</h4>
                                <p className="text-sm text-gray-500">Docket: {docketFull}</p>
                              </div>
                              <Badge variant="outline" className={
                                isSkipped
                                  ? 'bg-gray-100 text-gray-500 border-gray-300'
                                  : actionType === 'update'
                                    ? 'bg-blue-50 text-blue-700 border-blue-300'
                                    : 'bg-green-50 text-green-700 border-green-300'
                              }>
                                {actionType === 'update' && <RefreshCw className="w-3 h-3 mr-1" />}
                                {actionType === 'create' && <Plus className="w-3 h-3 mr-1" />}
                                {isSkipped ? 'Skipped' : actionType === 'update' ? 'Will Update' : 'Will Create'}
                              </Badge>
                            </div>

                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="font-medium text-gray-700">FMCSA Data:</p>
                                <p className="text-gray-600">Email: {carrier.email || 'N/A'}</p>
                                <p className="text-gray-600">Phone: {formatPhoneNumber(carrier.phone) || 'N/A'}</p>
                                <p className="text-gray-600">Status: {carrier.commonAuthorityStatus || 'N/A'}</p>
                              </div>
                              
                              {existingCarrier && (
                                <div>
                                  <p className="font-medium text-gray-700">Current Data:</p>
                                  <p className="text-gray-600">Email: {existingCarrier.email || 'N/A'}</p>
                                  <p className="text-gray-600">Phone: {formatPhoneNumber(existingCarrier.phone) || 'N/A'}</p>
                                  <p className="text-gray-600">Status: {existingCarrier.commonAuthorityStatus || 'N/A'}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <Separator />

              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  {(() => {
                    const createCount = Object.values(selectedActions).filter((a) => a === 'create').length;
                    const updateCount = Object.values(selectedActions).filter((a) => a === 'update').length;
                    const skipCount = Object.values(selectedActions).filter((a) => a === 'skip').length;
                    return (
                      <p>
                        {createCount > 0 && <span className="text-green-700 font-medium">{createCount} new</span>}
                        {createCount > 0 && (updateCount > 0 || skipCount > 0) && ', '}
                        {updateCount > 0 && <span className="text-blue-700 font-medium">{updateCount} update</span>}
                        {updateCount > 0 && skipCount > 0 && ', '}
                        {skipCount > 0 && <span className="text-gray-500">{skipCount} skipped</span>}
                      </p>
                    );
                  })()}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleApply}
                    disabled={applyChangesMutation.isPending || Object.values(selectedActions).every((a) => a === 'skip')}
                  >
                    {applyChangesMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Applying Changes...
                      </>
                    ) : (
                      'Apply Changes'
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
